import { AppBar, Theme, Toolbar, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../app/hooks';
import { MenuStages, Routes } from '../../constants/enums';
import {
  selectFeatureFlagHitl3,
  selectFeatureFlagMenuVersion,
  selectFeatureFlagTaskRouter,
  selectIsHitl7Enabled,
} from '../../redux/features/config/config.selector';
import { selectSessionId } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { AgentStatuses } from '../../redux/features/taskRouter/taskRouter.constants';
import { selectCurrentTask } from '../../redux/features/taskRouter/taskRouter.selector';
import { selectLoadedVersionDetails } from '../../selectors/menu';
import {
  selectActiveRestaurantDetails,
  selectedStageSelector,
} from '../../selectors/restaurant';
import { RolePermissions } from '../../types/restaurant';
import {
  hasRoleAccess,
  selectedRestaurantAccessSelector,
} from '../../utils/restaurants';
import HeaderIndicators from '../HeaderIndicators';
import { LocalTimeIndicator } from '../HeaderIndicators/LocalTimeIndicator';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ProfileMenu from './ProfileMenu';
import MenuVersionPicker from '../MenuVersionPicker';
import MenuVersionStage from '../MenuVersionStage';
import RestaurantsContainer from '../RestaurantsContainer';
import TaskRouterHeaderInfo from '../TaskRouterHeaderInfo';
import logger from '../../utils/logger';
import { copyTextToClipboard } from '../../utils';
import { HITL7_HEADER_GAP } from '../../utils/hitl3Spacing';

type HeaderProps = {
  isTaskRouterPage?: boolean;
  handleAgentAvailability: (agentStatus: AgentStatuses) => void;
  sendAgentEvent?: Function;
  logout: Function;
  sendHealthScore?: Function;
};

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    flexGrow: 1,
    // Never shrinks: the version + sessionId stay on one line and fully visible;
    // the restaurant picker is the element that yields width when space is tight.
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    whiteSpace: 'nowrap',
  },
  sessionId: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.75rem',
    // Always fully visible — never shrinks or truncates (the restaurant picker
    // yields width instead).
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  sessionIdText: {
    whiteSpace: 'nowrap',
  },
  restaurantPicker: {
    marginLeft: '4px',
  },
  menuVersionPicker: {
    marginLeft: '4px',
  },
  copyButton: {
    padding: theme.spacing(0.5),
    flexShrink: 0,
    '&:hover': {
      backgroundColor: `rgba(244, 244, 244, 0.5) !important`,
    },
  },
}));

const Header: FC<HeaderProps> = ({
  handleAgentAvailability,
  sendAgentEvent,
  logout,
  sendHealthScore,
}) => {
  const location = useLocation();
  const taskRouterFeatureFlag = useAppSelector(selectFeatureFlagTaskRouter);
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  const currentTaskId = useAppSelector(selectCurrentTask);

  const isTaskRouterPage =
    taskRouterFeatureFlag && location.pathname.indexOf(Routes.taskRouter) >= 0;

  const classes = useStyles();
  const { restaurantCode, primaryRestaurantCode } = useSelector(
    selectActiveRestaurantDetails
  );
  const selectedStage = useSelector(selectedStageSelector);
  const sessionId = useAppSelector(selectSessionId) || '';
  const selectedRestaurantAccessLevel = useSelector(
    selectedRestaurantAccessSelector
  );
  const hasMenuVersionAccess = hasRoleAccess(
    RolePermissions.RestaurantManager,
    selectedRestaurantAccessLevel
  );
  const FEATURE_FLAG_MENU_VERSION = useSelector(selectFeatureFlagMenuVersion);
  const loadedVersionDetails = useSelector(selectLoadedVersionDetails);
  const { isBrandMenu, menuVersion } = loadedVersionDetails || {};

  const canChangeMenuVersion =
    FEATURE_FLAG_MENU_VERSION &&
    hasMenuVersionAccess &&
    restaurantCode &&
    !currentTaskId &&
    !primaryRestaurantCode;

  return (
    <AppBar position="static" data-header="true">
      {/* Lock to one 64px line so the header can't exceed the height App.tsx
          subtracts (calc(100% - 64px)) and push the page into vertical scroll. */}
      <Toolbar
        // disableGutters drops MUI's default .MuiToolbar-gutters padding, which
        // is a media-query rule (16px / 24px ≥600px) that otherwise overrides a
        // plain `px` in sx. With it off, our 8px padding actually applies.
        disableGutters
        sx={{
          minHeight: '64px !important',
          height: 64,
          overflowY: 'hidden',
          // Stated explicitly rather than left to the cascade. Per CSS Overflow
          // 3, an axis left `visible` beside an axis that is not computes to
          // `auto`, so `overflowY: hidden` above has always implied a horizontal
          // scrollbar here — writing it down means the next person does not have
          // to know that rule to predict the behaviour.
          //
          // HITL-1667 set this to `hidden` to suppress the scrollbar, and that
          // was WRONG: suppressing the scrollbar did not make the row fit, it
          // only made the overflow invisible. The profile avatar is last in the
          // row, so it was silently clipped off the end — worse than a
          // scrollbar, because a scrollbar at least tells the agent there is
          // something they are not seeing. The row is given back real width
          // instead (the restaurant name's 32px, the avatar's 10px), and the
          // scrollbar stays as the honest fallback for a name long enough to
          // overflow anyway.
          //
          // Gated, because with 7.0 off this is the byte-identical flag-off
          // path: an `undefined` value is dropped during emotion's
          // serialization, so the generated class is unchanged and no Header
          // snapshot moves.
          overflowX: isHitl7 ? 'auto' : undefined,
          // 8px left edge padding (vs MUI's ~24px) and a single 4px gap between
          // every top-level item, so spacing is uniform. Right padding is 0 so
          // the last item (ProfileMenu) sits flush against the header's right
          // edge. Per-item margins/paddings are zeroed elsewhere so they don't
          // stack on top of this gap.
          // HITL-1662: 7.0 tightens the left inset and the inter-item gap. The
          // header must hold one row at 1024px with the session id AND the
          // restaurant code both shown in full — neither may truncate — so the
          // room comes out of the chrome around them.
          pl: isHitl7 ? '4px' : '8px',
          pr: 0,
          // HITL-1702: the gap comes back, 6px at 1024 growing to 8px from 1280
          // up, paid for by folding four indicators into the System icon.
          gap: isHitl7 ? HITL7_HEADER_GAP : '4px',
        }}
      >
        <img src="/presto_logo.svg" alt="Presto Logo" />
        <div
          className={classes.title}
          // Inline rather than a makeStyles rule: adding rules renumbers every
          // generated class name and moves unrelated snapshots. Undefined with
          // the flag off, so nothing changes there.
          style={isHitl7 ? { gap: '6px' } : undefined}
        >
          {!isTaskRouterPage && restaurantCode && sessionId ? (
            <Typography
              className={classes.sessionId}
              style={isHitl7 ? { gap: '2px' } : undefined}
              component="div"
              // sx (not the makeStyles class) so MUI's Typography variant can't
              // override the size — keeps the sessionId identical to the other
              // header text (version / BM / time).
              sx={{ fontSize: '0.75rem' }}
            >
              <span className={classes.sessionIdText}>{sessionId}</span>
              <IconButton
                className={classes.copyButton}
                size="small"
                onClick={() => copyTextToClipboard(sessionId, logger)}
                style={isHitl7 ? { padding: '2px' } : undefined}
              >
                <ContentCopyIcon fontSize="small" style={{ color: 'white' }} />
              </IconButton>
            </Typography>
          ) : null}
        </div>
        {!isTaskRouterPage && (
          <>
            <HeaderIndicators />
            {canChangeMenuVersion && (
              <div
                className={classes.restaurantPicker}
                style={isHitl7 ? { marginLeft: 0 } : undefined}
              >
                <MenuVersionStage logout={logout} />
              </div>
            )}
            <div
              className={classes.restaurantPicker}
              style={isHitl7 ? { marginLeft: 0 } : undefined}
            >
              <RestaurantsContainer
                logout={logout}
                sendHealthScore={sendHealthScore}
              />
            </div>
            <div
              className={classes.restaurantPicker}
              style={isHitl7 ? { marginLeft: 0 } : undefined}
            >
              {canChangeMenuVersion &&
              selectedStage === MenuStages.PLAYGROUND ? (
                <MenuVersionPicker logout={logout} />
              ) : isHitl7 ? (
                // HITL-1662: under 7.0 the build version and the restaurant's
                // local time stack in this one slot — version on top, clock
                // beneath. Both are standing reference text rather than live
                // indicators, so they belong together and out of the way; it
                // also returns the horizontal room the countdown now takes at
                // the head of the cluster.
                //
                // This is a separate branch rather than a conditional style on
                // one shared element on purpose: the flag-off markup below must
                // stay byte-identical, wrapper div included, or every Header
                // snapshot moves for a change nobody enabled.
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    lineHeight: 1.2,
                    // HITL-1662 tightened the header's inter-item gap to 1px so
                    // the row holds one line at 1024px, which left this slot
                    // butting straight up against the control to its left. One
                    // more pixel separates them and is all the row can spare.
                    marginLeft: '1px',
                  }}
                >
                  {menuVersion ? (
                    <span
                      style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                    >{`${isBrandMenu ? 'BM' : 'PRP'}: ${menuVersion}`}</span>
                  ) : null}
                  <LocalTimeIndicator />
                </div>
              ) : (
                <>
                  {menuVersion ? (
                    <span
                      style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                    >{`${isBrandMenu ? 'BM' : 'PRP'}: ${menuVersion}`}</span>
                  ) : null}
                </>
              )}
            </div>
          </>
        )}
        <ProfileMenu
          sendAgentEvent={sendAgentEvent}
          logout={logout}
          handleAgentAvailability={handleAgentAvailability}
          isTaskRouterPage={isTaskRouterPage}
        />
        {/* hitl_3_0 ON folds availability into ProfileMenu, so the standalone
            blue availability box is only rendered when the flag is OFF. */}
        {isTaskRouterPage && !isHitl3 && (
          <TaskRouterHeaderInfo
            handleAgentAvailability={handleAgentAvailability}
          />
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
